import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { paths } from '@/app/routing';

interface ReturnUrlState {
  /** The URL to return to after authentication */
  returnUrl: string | null;
  /** Set the return URL (typically called by guards before redirect) */
  setReturnUrl: (url: string | null) => void;
  /** Get and clear the return URL (called after successful auth) */
  consumeReturnUrl: () => string | null;
  /** Clear the return URL without consuming */
  clearReturnUrl: () => void;
}

/**
 * Return URL Store - Persists intended destination across auth redirects
 *
 * Flow:
 * 1. User visits protected route (e.g., /solo/dashboard)
 * 2. AuthGuard detects unauthenticated user
 * 3. Guard calls setReturnUrl('/solo/dashboard')
 * 4. Guard redirects to home/login
 * 5. User authenticates
 * 6. Auth callback calls consumeReturnUrl() to get destination
 * 7. User is redirected to /solo/dashboard
 *
 * The store persists to sessionStorage so the URL survives page refreshes
 * during the Auth0 redirect flow.
 */
export const useReturnUrlStore = create<ReturnUrlState>()(
  persist(
    (set, get) => ({
      returnUrl: null,

      setReturnUrl: (url: string | null) => {
        // Don't store invalid URLs or public routes
        if (!url || url === paths.home() || url === '/') {
          return;
        }
        set({ returnUrl: url });
      },

      consumeReturnUrl: () => {
        const url = get().returnUrl;
        set({ returnUrl: null });
        return url;
      },

      clearReturnUrl: () => {
        set({ returnUrl: null });
      },
    }),
    {
      name: 'return-url-storage',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);

/**
 * Utility function to get the appropriate redirect URL after authentication
 * based on user role and stored return URL.
 */
export const getPostAuthRedirectUrl = (
  role: string | null,
  consumeStoredUrl = true,
): string => {
  // Get stored return URL if consuming
  const storedUrl = consumeStoredUrl
    ? useReturnUrlStore.getState().consumeReturnUrl()
    : useReturnUrlStore.getState().returnUrl;

  // If we have a stored URL, validate it matches the user's role
  if (storedUrl) {
    const isClientUrl = storedUrl.startsWith('/client');
    const isProviderUrl =
      storedUrl.startsWith('/provider') ||
      storedUrl.startsWith('/solo') ||
      storedUrl.startsWith('/shops');

    // Only return stored URL if it matches the role
    if (role === 'client' && isClientUrl) {
      return storedUrl;
    }
    if (
      role &&
      ['shop_owner', 'independent_provider', 'staff_provider'].includes(role) &&
      isProviderUrl
    ) {
      return storedUrl;
    }
    // Clear invalid stored URL
    if (consumeStoredUrl) {
      useReturnUrlStore.getState().clearReturnUrl();
    }
  }

  // Default redirects based on role
  switch (role) {
    case 'client':
      return paths.client.dashboard();
    case 'shop_owner':
    case 'independent_provider':
    case 'staff_provider':
      return paths.auth.postLogin();
    case 'pending_provider':
      return paths.providerRegister();
    default:
      return paths.home();
  }
};
